/**
 * Party frames, and the little menu that runs the group.
 *
 * ## Why a frame column exists at all when there are nameplates
 * Three things the column says that a plate over somebody's head cannot. That a member is
 * **dead** — which wants a body walked to, not a heal, and confusing "dead" with "nearly dead"
 * costs a party the fight. That a member is **out of range**, so the numbers on their row are
 * the last ones the server saw and a heal aimed at them will not land. And **who leads**, since
 * only the leader may invite or remove and a group that cannot work out who that is asks the
 * wrong person twice. A nameplate is gone the moment its owner is behind a wall; the frame is
 * the only evidence they still exist.
 *
 * ## The portrait
 * Race and Calling, drawn as one 18 px glyph rather than a photograph per combination: two races
 * times two Callings is four images today and grows multiplicatively the moment a third of
 * either lands. The glyph is built from the same `look` word the crowd renderer draws the body
 * from (`unpackLook`), so the frame and the person can never show different classes.
 *
 * ## Everything here is a read
 * The frames send no commands. Clicking one selects that member as the target (which is how a
 * Warden heals without aiming); right-clicking one opens the menu, and every entry in the menu
 * is a slash command sent down the ordinary chat path — `/invite`, `/kick`, `/leave` — because
 * the server's party rules only ever accept those. There is no client-side party state: this
 * file draws the last `S2C.PartyState` it was handed and nothing else.
 */

import { PARTY_MAX_SIZE, PartyFlags, Race, unpackLook } from '@stillsun/shared';
import type { PartyMemberSnap, PartyStateMsg } from '@stillsun/shared';

/** Room-local player ids, the same numbers `InputCmd.targetId` carries. */
export interface PartyOptions {
  /** Clicking a frame selects that member — how a healer picks a target without aiming. */
  onSelect: (id: number) => void;
  /** A menu entry was chosen. The text is a slash command, sent verbatim on the chat path. */
  onCommand: (text: string) => void;
}

/** Vector paths for the four race/Calling portraits, on the same 24-unit grid as ui/icons.ts. */
const FACE_HUMAN =
  'M12 4.2a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8zM5.4 20.2c0-3.7 3-5.6 6.6-5.6s6.6 1.9 6.6 5.6';
const FACE_HOLLOW =
  'M12 3.6c-4 0-6.4 2.8-6.4 6.4 0 2.3 1 3.7 2.2 4.6l.4 3.2h7.6l.4-3.2c1.2-.9 2.2-2.3 2.2-4.6 0-3.6-2.4-6.4-6.4-6.4zM9.6 10.4h1.2M13.2 10.4h1.2M11 14h2';
/** The Calling mark, drawn small beside the head: a tower shield, or a lantern. */
const MARK_VANGUARD = 'M18.6 3.4l3.2 1.2v3.6c0 2.8-1.5 4.6-3.2 5.4-1.7-.8-3.2-2.6-3.2-5.4V4.6z';
const MARK_WARDEN = 'M18.6 3.2v1.6M16.6 5.4h4M17 5.4l-.6 4.4h4.4L20.2 5.4M18.6 6.8v1.8';

const CROWN = 'M2 10.6 4.2 3.4l3.4 3.6L11 1.6l3.4 5.4 3.4-3.6 2.2 7.2z';

interface Frame {
  root: HTMLButtonElement;
  face: SVGSVGElement;
  facePath: SVGPathElement;
  markPath: SVGPathElement;
  name: HTMLElement;
  level: HTMLElement;
  fill: HTMLElement;
  hpNum: HTMLElement;
  /** Last drawn values, so a periodic refresh only writes the DOM when something moved. */
  lastId: number;
  lastName: string;
  lastLook: number;
  lastLevel: number;
  lastHp: number;
  lastMaxHp: number;
  lastFlags: number;
  lastLeader: boolean;
  lastTarget: boolean;
}

const svg = (tag: string): SVGElement =>
  document.createElementNS('http://www.w3.org/2000/svg', tag);

export class PartyPanel {
  private readonly root: HTMLDivElement;
  private readonly menu: HTMLDivElement;
  private readonly frames: Frame[] = [];

  /** The last state the server sent. The panel never invents membership. */
  private leaderId = 0;
  private members: PartyMemberSnap[] = [];
  private selfId = 0;
  private targetId = 0;
  private visible = false;

  constructor(private readonly opts: PartyOptions) {
    this.root = need('party');
    this.menu = need('partymenu');

    // Built once at the cap and hidden as needed: a party of five is the ceiling, so there is
    // nothing to gain from creating rows lazily and a row's worth of churn to lose every time
    // somebody joins mid-fight. `PARTY_MAX_SIZE - 1` because the viewer's own health is the
    // WoW bar and is never one of these rows.
    for (let i = 0; i < PARTY_MAX_SIZE - 1; i++) {
      const frame = this.build();
      this.frames.push(frame);
      this.root.appendChild(frame.root);
    }

    // One listener for the whole menu, and one for anywhere else: a menu that outlives the
    // click that opened it is the single most irritating bug this kind of widget has.
    this.menu.addEventListener('mousedown', (ev) => {
      ev.stopPropagation();
    });
    window.addEventListener('mousedown', () => {
      this.closeMenu();
    });
    window.addEventListener('blur', () => {
      this.closeMenu();
    });
  }

  private build(): Frame {
    const root = document.createElement('button');
    root.type = 'button';
    root.className = 'pm';
    root.setAttribute('role', 'listitem');
    root.style.display = 'none';

    const top = document.createElement('div');
    top.className = 'pm-top';

    const face = svg('svg') as SVGSVGElement;
    face.setAttribute('class', 'pm-face');
    face.setAttribute('viewBox', '0 0 24 24');
    face.setAttribute('aria-hidden', 'true');
    const facePath = svg('path') as SVGPathElement;
    const markPath = svg('path') as SVGPathElement;
    face.append(facePath, markPath);

    const name = document.createElement('span');
    name.className = 'pm-name';
    const crown = svg('svg') as SVGSVGElement;
    crown.setAttribute('class', 'pm-crown');
    crown.setAttribute('viewBox', '0 0 22 13');
    crown.setAttribute('aria-hidden', 'true');
    const crownPath = svg('path') as SVGPathElement;
    crownPath.setAttribute('d', CROWN);
    crown.appendChild(crownPath);
    const level = document.createElement('span');
    level.className = 'pm-lvl';
    top.append(face, name, crown, level);

    const hp = document.createElement('div');
    hp.className = 'pm-hp';
    const fill = document.createElement('div');
    fill.className = 'pm-fill';
    const hpNum = document.createElement('div');
    hpNum.className = 'pm-hpnum';
    hp.append(fill, hpNum);

    root.append(top, hp);

    root.addEventListener('click', () => {
      const id = Number(root.dataset['id'] ?? '0');
      if (id > 0) this.opts.onSelect(id);
    });
    root.addEventListener('contextmenu', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const id = Number(root.dataset['id'] ?? '0');
      if (id > 0) this.openMenuFor(id, ev.clientX, ev.clientY);
    });

    return {
      root,
      face,
      facePath,
      markPath,
      name,
      level,
      fill,
      hpNum,
      lastId: -1,
      lastName: '',
      lastLook: -1,
      lastLevel: -1,
      lastHp: -1,
      lastMaxHp: -1,
      lastFlags: -1,
      lastLeader: false,
      lastTarget: false,
    };
  }

  /** Who the viewer is. Needed to keep their own row out of the column. */
  setSelfId(id: number): void {
    this.selfId = id;
  }

  /** The current target, so the frame the player is healing reads as chosen. */
  setTargetId(id: number): void {
    if (this.targetId === id) return;
    this.targetId = id;
    this.draw();
  }

  /** A fresh `S2C.PartyState`. An empty member list is how a leave, a kick and a disband arrive. */
  setState(msg: PartyStateMsg): void {
    this.leaderId = msg.leaderId;
    this.members = msg.members;
    if (msg.members.length === 0) this.closeMenu();
    this.draw();
  }

  /** Leaving a room (a gate crossing, a kick, the hub) empties the column. */
  clear(): void {
    this.leaderId = 0;
    this.members = [];
    this.closeMenu();
    this.draw();
  }

  /** The HUD is up or it is not; the column follows the WoW bar exactly. */
  setVisible(on: boolean): void {
    if (this.visible === on) return;
    this.visible = on;
    if (!on) this.closeMenu();
    // `draw` owns the class: whether the column is shown is "the HUD is up **and** there is
    // somebody other than me in it", and only one place should be allowed to decide that.
    this.draw();
  }

  private draw(): void {
    const shown = this.members.filter((m) => m.id !== this.selfId);
    this.root.classList.toggle('on', this.visible && shown.length > 0);

    for (let i = 0; i < this.frames.length; i++) {
      const frame = this.frames[i];
      if (!frame) continue;
      const m = shown[i];
      if (m === undefined) {
        if (frame.lastId !== 0) {
          frame.root.style.display = 'none';
          frame.lastId = 0;
        }
        continue;
      }
      if (frame.lastId !== m.id) {
        frame.root.style.display = 'block';
        frame.root.dataset['id'] = String(m.id);
        frame.lastId = m.id;
      }
      if (frame.lastName !== m.name) {
        frame.name.textContent = m.name;
        frame.lastName = m.name;
      }
      if (frame.lastLevel !== m.level) {
        frame.level.textContent = String(m.level);
        frame.lastLevel = m.level;
      }
      if (frame.lastLook !== m.look) {
        const look = unpackLook(m.look);
        frame.facePath.setAttribute('d', look.race === Race.Hollow ? FACE_HOLLOW : FACE_HUMAN);
        // Calling 2 is the Warden (the lantern); everything else draws the Vanguard's shield,
        // including an unset Calling — a blank corner reads as a broken icon, not as "none".
        frame.markPath.setAttribute('d', look.calling === 2 ? MARK_WARDEN : MARK_VANGUARD);
        frame.lastLook = m.look;
      }

      const dead = (m.flags & (PartyFlags.Dead | PartyFlags.Downed)) !== 0;
      const out = (m.flags & PartyFlags.InRange) === 0;
      if (frame.lastFlags !== m.flags) {
        frame.root.classList.toggle('dead', dead);
        frame.root.classList.toggle('out', out);
        frame.lastFlags = m.flags;
      }
      const leader = m.id === this.leaderId;
      if (frame.lastLeader !== leader) {
        frame.root.classList.toggle('leader', leader);
        frame.lastLeader = leader;
      }
      const targeted = m.id === this.targetId;
      if (frame.lastTarget !== targeted) {
        frame.root.classList.toggle('target', targeted);
        frame.lastTarget = targeted;
      }

      if (frame.lastHp !== m.hp || frame.lastMaxHp !== m.maxHp || dead) {
        const fraction = m.maxHp > 0 ? Math.max(0, Math.min(1, m.hp / m.maxHp)) : 0;
        frame.fill.style.transform = `scaleX(${dead ? 1 : fraction.toFixed(3)})`;
        // A spirit is stated in words, never implied by an empty bar: "dead" and "nearly dead"
        // want completely different things done about them.
        frame.hpNum.textContent = dead
          ? (m.flags & PartyFlags.Dead) !== 0
            ? 'dead'
            : 'downed'
          : `${String(Math.max(0, Math.round(m.hp)))} / ${String(Math.round(m.maxHp))}`;
        frame.lastHp = m.hp;
        frame.lastMaxHp = m.maxHp;
      }

      frame.root.setAttribute(
        'aria-label',
        `${m.name}, level ${String(m.level)}` +
          (leader ? ', group leader' : '') +
          (dead
            ? ', dead'
            : `, ${String(Math.round((m.hp / Math.max(1, m.maxHp)) * 100))}% health`) +
          (out ? ', out of range' : ''),
      );
    }
  }

  // ------------------------------------------------------------------ the menu

  /**
   * Right-click on a Waker in the world. `name` is used for `/invite`, which the server matches
   * by name because a room-local id means nothing to a player and everything to a griefer.
   */
  openForPlayer(id: number, name: string, x: number, y: number): void {
    this.openMenu(id, name, x, y);
  }

  private openMenuFor(id: number, x: number, y: number): void {
    const m = this.members.find((p) => p.id === id);
    this.openMenu(id, m?.name ?? '', x, y);
  }

  private openMenu(id: number, name: string, x: number, y: number): void {
    if (id === 0 || id === this.selfId) return;
    const inParty = this.members.some((m) => m.id === id);
    const iLead = this.leaderId === this.selfId && this.leaderId !== 0;
    const grouped = this.members.length > 1;

    this.menu.textContent = '';
    if (name !== '') {
      const who = document.createElement('div');
      who.className = 'pmenu-who';
      who.textContent = name;
      this.menu.appendChild(who);
    }

    const add = (label: string, run: () => void, warn = false): void => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      if (warn) b.className = 'warn';
      b.addEventListener('click', () => {
        this.closeMenu();
        run();
      });
      this.menu.appendChild(b);
    };
    const say = (command: string) => () => {
      this.opts.onCommand(command);
    };

    add('Select', () => {
      this.opts.onSelect(id);
    });
    if (!inParty && name !== '') {
      // Offered even when the viewer does not lead: the server answers "Only the group leader
      // can invite", which is a better teacher than a menu entry that silently is not there.
      add('Invite', say(`/invite ${name}`));
    }
    if (inParty && iLead && name !== '') add('Remove from group', say(`/kick ${name}`), true);
    if (grouped) add('Leave group', say('/leave'), true);

    this.menu.classList.add('on');
    this.menu.setAttribute('aria-hidden', 'false');
    // Placed after it is measurable, and nudged back inside the window: a menu opened near the
    // right edge would otherwise hang off the screen with its only useful entry unreachable.
    const rect = this.menu.getBoundingClientRect();
    const left = Math.min(x, window.innerWidth - rect.width - 6);
    const top = Math.min(y, window.innerHeight - rect.height - 6);
    this.menu.style.left = `${String(Math.max(4, left))}px`;
    this.menu.style.top = `${String(Math.max(4, top))}px`;
  }

  closeMenu(): void {
    if (!this.menu.classList.contains('on')) return;
    this.menu.classList.remove('on');
    this.menu.setAttribute('aria-hidden', 'true');
  }

  /** True while the menu is up, so ESC can close it before it reaches the target or a panel. */
  get menuOpen(): boolean {
    return this.menu.classList.contains('on');
  }
}

function need<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}
